import { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Send, Phone, Mail, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    message: '',
  });

  const projectTypes = language === 'fr' 
    ? [
        { value: '', label: 'Sélectionnez un type de projet' },
        { value: 'strategie-social-media', label: 'Stratégie Social Media' },
        { value: 'production-contenu', label: 'Production de contenu' },
        { value: 'identite-visuelle', label: 'Identité visuelle' },
        { value: 'audit-digital', label: 'Audit digital' },
        { value: 'autre', label: 'Autre' },
      ]
    : [
        { value: '', label: 'Select a project type' },
        { value: 'strategie-social-media', label: 'Social Media Strategy' },
        { value: 'production-contenu', label: 'Content Production' },
        { value: 'identite-visuelle', label: 'Visual Identity' },
        { value: 'audit-digital', label: 'Digital Audit' },
        { value: 'autre', label: 'Other' },
      ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(t('contact.success'));
      setFormData({ name: '', email: '', phone: '', projectType: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(t('contact.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section 
      id="contact"
      ref={ref}
      className="py-24 md:py-32 bg-white"
      data-testid="contact-section"
    >
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left Side - Info */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-xs font-semibold tracking-[0.3em] uppercase text-neutral-500 mb-4">
              {language === 'fr' ? 'Parlons de votre projet' : 'Let\'s talk about your project'}
            </span>
            <h2 className="heading-xl text-4xl md:text-5xl lg:text-6xl text-black mb-6" data-testid="contact-title">
              {t('contact.title')}
            </h2>
            <p className="text-neutral-600 text-lg mb-10 leading-relaxed">
              {language === 'fr'
                ? 'Vous avez un projet en tête ? Nous serions ravis d\'en discuter. Remplissez le formulaire et nous vous recontacterons dans les plus brefs délais.'
                : 'Have a project in mind? We\'d love to discuss it. Fill out the form and we\'ll get back to you as soon as possible.'}
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-4">
              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center gap-4 p-4 bg-neutral-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">{language === 'fr' ? 'Email' : 'Email'}</p>
                  <p className="font-semibold text-black">contact@bao-agency.com</p>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center gap-4 p-4 bg-neutral-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">{language === 'fr' ? 'Téléphone' : 'Phone'}</p>
                  <p className="font-semibold text-black">+33 1 23 45 67 89</p>
                </div>
              </motion.div>

              <motion.div
                whileHover={{ x: 5 }}
                className="flex items-center gap-4 p-4 bg-neutral-50 rounded-xl"
              >
                <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm text-neutral-500">{language === 'fr' ? 'Adresse' : 'Address'}</p>
                  <p className="font-semibold text-black">Paris, France</p>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Right Side - Form */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-neutral-50 rounded-3xl p-8 md:p-10"
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-black mb-2">
                  {language === 'fr' ? 'Nom complet' : 'Full name'} *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={t('contact.namePlaceholder')}
                  className="w-full px-4 py-4 rounded-xl border border-neutral-200 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                  data-testid="contact-name-input"
                />
              </div>

              {/* Email & Phone Row */}
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-black mb-2">
                    {t('contact.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder={t('contact.emailPlaceholder')}
                    className="w-full px-4 py-4 rounded-xl border border-neutral-200 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-black mb-2">
                    {language === 'fr' ? 'Téléphone' : 'Phone'}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full px-4 py-4 rounded-xl border border-neutral-200 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none transition-all"
                    data-testid="contact-phone-input"
                  />
                </div>
              </div>

              {/* Project Type Dropdown */}
              <div>
                <label htmlFor="projectType" className="block text-sm font-semibold text-black mb-2">
                  {language === 'fr' ? 'Type de projet' : 'Project type'} *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-4 rounded-xl border border-neutral-200 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none transition-all appearance-none cursor-pointer"
                  data-testid="contact-project-type-select"
                >
                  {projectTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-black mb-2">
                  {t('contact.message')} *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder={t('contact.messagePlaceholder')}
                  className="w-full px-4 py-4 rounded-xl border border-neutral-200 bg-white focus:border-black focus:ring-1 focus:ring-black outline-none transition-all resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isLoading}
                whileHover={{ scale: 1.02, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}
                whileTap={{ scale: 0.98 }}
                className="w-full btn-pill bg-black text-white font-semibold py-5 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isLoading ? (
                  <span className="animate-pulse">{language === 'fr' ? 'Envoi en cours...' : 'Sending...'}</span>
                ) : (
                  <>
                    {language === 'fr' ? 'Envoyer ma demande' : 'Send my request'}
                    <Send className="w-5 h-5" />
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
